// src/store/slices/billingSlice.ts - UPDATED VERSION

/* eslint-disable @typescript-eslint/no-explicit-any */
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  PricingPageData,
  SubscriptionResponse,
  UsageSummary,
  BillingProfileResponse,
  ProrationPreview,
  PlanChangeRequest,
  CreateCheckoutSessionRequest,
  CreateCreditsCheckoutRequest,
  CancelSubscriptionRequest,
  BillingProfileUpdate,
  InvoiceResponse,
  PaymentMethodResponse
} from '@/models/Billing';
import { billingService } from '@/services/billingService';

interface BillingState {
  // Individual data pieces
  subscription: SubscriptionResponse | null;
  usage: UsageSummary | null;
  billingProfile: BillingProfileResponse | null;
  pricingData: PricingPageData | null;
  invoices: InvoiceResponse[];
  paymentMethods: PaymentMethodResponse[];

  // Plan change flow
  prorationPreview: ProrationPreview | null;
  isProrationLoading: boolean; // NEW: Track proration preview loading
  isChangingPlan: boolean;

  // Checkout flow
  isProcessingCheckout: boolean;

  // Individual loading states
  isSubscriptionLoading: boolean;
  isUsageLoading: boolean;
  isBillingProfileLoading: boolean;
  isPricingLoading: boolean;
  isInvoicesLoading: boolean;
  isPaymentMethodsLoading: boolean;

  // Error states
  subscriptionError: string | null;
  usageError: string | null;
  billingProfileError: string | null;
  pricingError: string | null;
  invoicesError: string | null;
  paymentMethodsError: string | null;
  generalError: string | null;
}

const initialState: BillingState = {
  subscription: null,
  usage: null,
  billingProfile: null,
  pricingData: null,
  invoices: [],
  paymentMethods: [],
  prorationPreview: null,
  isProrationLoading: false, // NEW
  isChangingPlan: false,
  isProcessingCheckout: false,
  isSubscriptionLoading: false,
  isUsageLoading: false,
  isBillingProfileLoading: false,
  isPricingLoading: false,
  isInvoicesLoading: false,
  isPaymentMethodsLoading: false,
  subscriptionError: null,
  usageError: null,
  billingProfileError: null,
  pricingError: null,
  invoicesError: null,
  paymentMethodsError: null,
  generalError: null,
};

// =================== Async Thunks ===================

export const fetchPricingData = createAsyncThunk(
  'billing/fetchPricingData',
  async (_, { rejectWithValue }) => {
    try {
      const data = await billingService.getPricingData();
      return data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch pricing data');
    }
  }
);

export const fetchSubscription = createAsyncThunk(
  'billing/fetchSubscription',
  async (_, { rejectWithValue }) => {
    try {
      const subscription = await billingService.getSubscription();
      return subscription;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch subscription');
    }
  }
);

export const fetchBillingProfile = createAsyncThunk(
  'billing/fetchBillingProfile',
  async (_, { rejectWithValue }) => {
    try {
      const profile = await billingService.getBillingProfile();
      return profile;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch billing profile');
    }
  }
);

export const fetchInvoices = createAsyncThunk(
  'billing/fetchInvoices',
  async (limit: number = 20, { rejectWithValue }) => {
    try {
      const invoices = await billingService.getInvoices(limit);
      return invoices;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch invoices');
    }
  }
);

export const fetchPaymentMethods = createAsyncThunk(
  'billing/fetchPaymentMethods',
  async (_, { rejectWithValue }) => {
    try {
      const methods = await billingService.getPaymentMethods();
      return methods;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch payment methods');
    }
  }
);

export const createCheckoutSession = createAsyncThunk(
  'billing/createCheckoutSession',
  async (request: CreateCheckoutSessionRequest, { rejectWithValue }) => {
    try {
      const response = await billingService.createCheckoutSession(request);
      billingService.redirectToCheckout(response.session_id);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create checkout session');
    }
  }
);

export const createCreditsCheckoutSession = createAsyncThunk(
  'billing/createCreditsCheckoutSession',
  async (request: CreateCreditsCheckoutRequest, { rejectWithValue }) => {
    try {
      const response = await billingService.createCreditsCheckoutSession(request);
      billingService.redirectToCheckout(response.session_id);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create credits checkout session');
    }
  }
);

export const fetchProrationPreview = createAsyncThunk(
  'billing/fetchProrationPreview',
  async (newPriceId: string, { rejectWithValue }) => {
    try {
      const preview = await billingService.getProrationPreview(newPriceId);
      return preview;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch proration preview');
    }
  }
);

export const changePlan = createAsyncThunk(
  'billing/changePlan',
  async (request: PlanChangeRequest, { rejectWithValue }) => {
    try {
      const response = await billingService.changeSubscriptionPlan(request);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to change plan');
    }
  }
);

export const cancelSubscription = createAsyncThunk(
  'billing/cancelSubscription',
  async (request: CancelSubscriptionRequest, { rejectWithValue }) => {
    try {
      const response = await billingService.cancelSubscription(request);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to cancel subscription');
    }
  }
);

export const reactivateSubscription = createAsyncThunk(
  'billing/reactivateSubscription',
  async (_, { rejectWithValue }) => {
    try {
      const response = await billingService.reactivateSubscription();
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to reactivate subscription');
    }
  }
);

export const cancelScheduledPlanChange = createAsyncThunk(
  'billing/cancelScheduledPlanChange',
  async (scheduleId: string, { rejectWithValue }) => {
    try {
      const response = await billingService.cancelScheduledPlanChange(scheduleId);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to cancel scheduled plan change');
    }
  }
);

export const updateBillingProfile = createAsyncThunk(
  'billing/updateBillingProfile',
  async (profileUpdate: BillingProfileUpdate, { rejectWithValue }) => {
    try {
      const response = await billingService.updateBillingProfile(profileUpdate);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update billing profile');
    }
  }
);

// =================== Slice ===================

const billingSlice = createSlice({
  name: 'billing',
  initialState,
  reducers: {
    clearErrors: (state) => {
      state.subscriptionError = null;
      state.usageError = null;
      state.billingProfileError = null;
      state.pricingError = null;
      state.invoicesError = null;
      state.paymentMethodsError = null;
      state.generalError = null;
    },

    clearProrationPreview: (state) => {
      state.prorationPreview = null;
      state.isProrationLoading = false; // Also clear loading state
    },

    updateSubscriptionStatus: (state, action: PayloadAction<SubscriptionResponse>) => {
      state.subscription = action.payload;
    },

    updateUsageSummary: (state, action: PayloadAction<UsageSummary>) => {
      state.usage = action.payload;
    }
  },

  extraReducers: (builder) => {
    // Pricing data
    builder
      .addCase(fetchPricingData.pending, (state) => {
        state.isPricingLoading = true;
        state.pricingError = null;
      })
      .addCase(fetchPricingData.fulfilled, (state, action) => {
        state.isPricingLoading = false;
        state.pricingData = action.payload;
      })
      .addCase(fetchPricingData.rejected, (state, action) => {
        state.isPricingLoading = false;
        state.pricingError = action.payload as string;
      })

      // Subscription
      .addCase(fetchSubscription.pending, (state) => {
        state.isSubscriptionLoading = true;
        state.subscriptionError = null;
      })
      .addCase(fetchSubscription.fulfilled, (state, action) => {
        state.isSubscriptionLoading = false;
        state.subscription = action.payload;
      })
      .addCase(fetchSubscription.rejected, (state, action) => {
        state.isSubscriptionLoading = false;
        state.subscriptionError = action.payload as string;
      })

      // Billing profile
      .addCase(fetchBillingProfile.pending, (state) => {
        state.isBillingProfileLoading = true;
        state.billingProfileError = null;
      })
      .addCase(fetchBillingProfile.fulfilled, (state, action) => {
        state.isBillingProfileLoading = false;
        state.billingProfile = action.payload;
      })
      .addCase(fetchBillingProfile.rejected, (state, action) => {
        state.isBillingProfileLoading = false;
        state.billingProfileError = action.payload as string;
      })

      // Invoices
      .addCase(fetchInvoices.pending, (state) => {
        state.isInvoicesLoading = true;
        state.invoicesError = null;
      })
      .addCase(fetchInvoices.fulfilled, (state, action) => {
        state.isInvoicesLoading = false;
        state.invoices = action.payload;
      })
      .addCase(fetchInvoices.rejected, (state, action) => {
        state.isInvoicesLoading = false;
        state.invoicesError = action.payload as string;
      })

      // Payment methods
      .addCase(fetchPaymentMethods.pending, (state) => {
        state.isPaymentMethodsLoading = true;
        state.paymentMethodsError = null;
      })
      .addCase(fetchPaymentMethods.fulfilled, (state, action) => {
        state.isPaymentMethodsLoading = false;
        state.paymentMethods = action.payload;
      })
      .addCase(fetchPaymentMethods.rejected, (state, action) => {
        state.isPaymentMethodsLoading = false;
        state.paymentMethodsError = action.payload as string;
      })

      // Checkout sessions
      .addCase(createCheckoutSession.pending, (state) => {
        state.isProcessingCheckout = true;
        state.generalError = null;
      })
      .addCase(createCheckoutSession.fulfilled, (state) => {
        state.isProcessingCheckout = false;
      })
      .addCase(createCheckoutSession.rejected, (state, action) => {
        state.isProcessingCheckout = false;
        state.generalError = action.payload as string;
      })

      .addCase(createCreditsCheckoutSession.pending, (state) => {
        state.isProcessingCheckout = true;
        state.generalError = null;
      })
      .addCase(createCreditsCheckoutSession.fulfilled, (state) => {
        state.isProcessingCheckout = false;
      })
      .addCase(createCreditsCheckoutSession.rejected, (state, action) => {
        state.isProcessingCheckout = false;
        state.generalError = action.payload as string;
      })

      .addCase(fetchProrationPreview.pending, (state) => {
        state.isProrationLoading = true; // NEW: Set loading state
        state.prorationPreview = null; // Clear old preview while loading
      })
      .addCase(fetchProrationPreview.fulfilled, (state, action) => {
        state.isProrationLoading = false; // NEW: Clear loading state
        state.prorationPreview = action.payload;
      })
      .addCase(fetchProrationPreview.rejected, (state, action) => {
        state.isProrationLoading = false; // NEW: Clear loading state on error
        state.generalError = action.payload as string;
      })

      // Plan change
      .addCase(changePlan.pending, (state) => {
        state.isChangingPlan = true;
        state.generalError = null;
      })
      .addCase(changePlan.fulfilled, (state, action) => {
        state.isChangingPlan = false;
        state.subscription = action.payload;
        state.prorationPreview = null;
        state.isProrationLoading = false; // Ensure loading state is cleared
      })
      .addCase(changePlan.rejected, (state, action) => {
        state.isChangingPlan = false;
        state.generalError = action.payload as string;
      })

      // Subscription cancellation
      .addCase(cancelSubscription.pending, (state) => {
        state.isChangingPlan = true;
        state.generalError = null;
      })
      .addCase(cancelSubscription.fulfilled, (state, action) => {
        state.isChangingPlan = false;
        state.subscription = action.payload;
      })
      .addCase(cancelSubscription.rejected, (state, action) => {
        state.isChangingPlan = false;
        state.generalError = action.payload as string;
      })

      // Subscription reactivation
      .addCase(reactivateSubscription.pending, (state) => {
        state.isChangingPlan = true;
        state.generalError = null;
      })
      .addCase(reactivateSubscription.fulfilled, (state, action) => {
        state.isChangingPlan = false;
        state.subscription = action.payload;
      })
      .addCase(reactivateSubscription.rejected, (state, action) => {
        state.isChangingPlan = false;
        state.generalError = action.payload as string;
      })

      // Cancel scheduled plan change
      .addCase(cancelScheduledPlanChange.pending, (state) => {
        state.isChangingPlan = true;
        state.generalError = null;
      })
      .addCase(cancelScheduledPlanChange.fulfilled, (state, action) => {
        state.isChangingPlan = false;
        state.subscription = action.payload;
      })
      .addCase(cancelScheduledPlanChange.rejected, (state, action) => {
        state.isChangingPlan = false;
        state.generalError = action.payload as string;
      })

      // Billing profile update
      .addCase(updateBillingProfile.fulfilled, (state, action) => {
        state.billingProfile = action.payload;
      });
  },
});

export const {
  clearErrors,
  clearProrationPreview,
  updateSubscriptionStatus,
  updateUsageSummary
} = billingSlice.actions;

export default billingSlice.reducer;
export type { BillingState };

// =================== Selectors ===================

export const selectBillingState = (state: { billing: BillingState }) => state.billing;
export const selectPricingData = (state: { billing: BillingState }) => state.billing.pricingData;
export const selectSubscription = (state: { billing: BillingState }) => state.billing.subscription;
export const selectUsageSummary = (state: { billing: BillingState }) => state.billing.usage;
export const selectBillingProfile = (state: { billing: BillingState }) => state.billing.billingProfile;
export const selectInvoices = (state: { billing: BillingState }) => state.billing.invoices;
export const selectPaymentMethods = (state: { billing: BillingState }) => state.billing.paymentMethods;
export const selectProrationPreview = (state: { billing: BillingState }) => state.billing.prorationPreview;

export const selectIsProcessingCheckout = (state: { billing: BillingState }) => state.billing.isProcessingCheckout;
export const selectIsChangingPlan = (state: { billing: BillingState }) => state.billing.isChangingPlan;
export const selectIsProrationLoading = (state: { billing: BillingState }) => state.billing.isProrationLoading; // NEW selector

export const selectIsSubscriptionLoading = (state: { billing: BillingState }) => state.billing.isSubscriptionLoading;
export const selectIsUsageLoading = (state: { billing: BillingState }) => state.billing.isUsageLoading;
export const selectIsBillingProfileLoading = (state: { billing: BillingState }) => state.billing.isBillingProfileLoading;
export const selectIsPricingLoading = (state: { billing: BillingState }) => state.billing.isPricingLoading;
export const selectIsInvoicesLoading = (state: { billing: BillingState }) => state.billing.isInvoicesLoading;
export const selectIsPaymentMethodsLoading = (state: { billing: BillingState }) => state.billing.isPaymentMethodsLoading;

export const selectSubscriptionError = (state: { billing: BillingState }) => state.billing.subscriptionError;
export const selectUsageError = (state: { billing: BillingState }) => state.billing.usageError;
export const selectBillingProfileError = (state: { billing: BillingState }) => state.billing.billingProfileError;
export const selectPricingError = (state: { billing: BillingState }) => state.billing.pricingError;
export const selectInvoicesError = (state: { billing: BillingState }) => state.billing.invoicesError;
export const selectPaymentMethodsError = (state: { billing: BillingState }) => state.billing.paymentMethodsError;
export const selectGeneralError = (state: { billing: BillingState }) => state.billing.generalError;

// Compound selectors
export const selectHasAnyError = (state: { billing: BillingState }) => {
  const billing = state.billing;
  return !!(
    billing.subscriptionError ||
    billing.usageError ||
    billing.billingProfileError ||
    billing.pricingError ||
    billing.invoicesError ||
    billing.paymentMethodsError ||
    billing.generalError
  );
};

export const selectIsAnyLoading = (state: { billing: BillingState }) => {
  const billing = state.billing;
  return (
    billing.isSubscriptionLoading ||
    billing.isUsageLoading ||
    billing.isBillingProfileLoading ||
    billing.isPricingLoading ||
    billing.isInvoicesLoading ||
    billing.isPaymentMethodsLoading ||
    billing.isProrationLoading
  );
};